/**
 * Show API messages in the user's language. The API answers in English;
 * known messages are looked up in the catalogue, zod's generic validation
 * messages are matched by pattern, anything else stays as it is.
 */
import i18n from "../i18n/index.js";
import { apiMessages } from "../i18n/api-messages.js";

type Locale = keyof typeof apiMessages;

/** zod's built-in messages, e.g. "String must contain at most 120 character(s)". */
const PATTERNS: Array<[RegExp, Record<Locale, string>]> = [
  [
    /^Required$/,
    { de: "Pflichtfeld", fr: "Champ obligatoire", es: "Campo obligatorio" },
  ],
  [
    /^Invalid email$/,
    {
      de: "Ungültige E-Mail-Adresse",
      fr: "Adresse e-mail invalide",
      es: "Correo electrónico no válido",
    },
  ],
  [
    /^Invalid url$/,
    { de: "Ungültige URL", fr: "URL invalide", es: "URL no válida" },
  ],
  [
    /^Invalid (date|datetime)$/,
    { de: "Ungültiges Datum", fr: "Date invalide", es: "Fecha no válida" },
  ],
  [
    /^(Invalid uuid|Invalid enum value.*|Invalid input)$/,
    {
      de: "Ungültige Auswahl",
      fr: "Sélection invalide",
      es: "Selección no válida",
    },
  ],
  [
    /^Expected .+, received .+$/,
    { de: "Ungültiger Wert", fr: "Valeur invalide", es: "Valor no válido" },
  ],
  [
    /^String must contain at least (\d+) character\(s\)$/,
    {
      de: "Mindestens $1 Zeichen",
      fr: "Au moins $1 caractères",
      es: "Al menos $1 caracteres",
    },
  ],
  [
    /^String must contain at most (\d+) character\(s\)$/,
    {
      de: "Höchstens $1 Zeichen",
      fr: "Au plus $1 caractères",
      es: "Como máximo $1 caracteres",
    },
  ],
  [
    /^Number must be greater than or equal to (-?[\d.]+)$/,
    { de: "Mindestens $1", fr: "Au moins $1", es: "Como mínimo $1" },
  ],
  [
    /^Number must be less than or equal to (-?[\d.]+)$/,
    { de: "Höchstens $1", fr: "Au plus $1", es: "Como máximo $1" },
  ],
  [
    /^Number must be greater than (-?[\d.]+)$/,
    {
      de: "Muss grösser als $1 sein",
      fr: "Doit être supérieur à $1",
      es: "Debe ser mayor que $1",
    },
  ],
  [
    /^Array must contain at least (\d+) element\(s\)$/,
    {
      de: "Mindestens $1 Einträge",
      fr: "Au moins $1 éléments",
      es: "Al menos $1 elementos",
    },
  ],
];

function currentLocale(): Locale | null {
  const language = (i18n.resolvedLanguage ?? i18n.language ?? "en").slice(0, 2);
  return language === "de" || language === "fr" || language === "es"
    ? language
    : null;
}

export function translateApiMessage(message: string): string {
  const locale = currentLocale();
  if (!locale) return message;
  const known = apiMessages[locale][message];
  if (known) return known;
  for (const [pattern, translations] of PATTERNS)
    if (pattern.test(message))
      return message.replace(pattern, translations[locale]);
  return message;
}
